import { cx } from '@vizit/theme';
import * as React from 'react';
import { Button as AriaButton, composeRenderProps } from 'react-aria-components';
import { useTooltipDisabledProps } from '../../utils/use-tooltip-disabled-props';
import { RadialProgress } from '../radial-progress';
import { hoverActionButtonClasses } from './styles';
import { HoverActionButtonHierarchy, HoverActionButtonSize } from './types';
import type { HoverActionButtonProps } from './types';

/**
 * Buttons / Hover Action: an icon-only square action button that surfaces on
 * hover over media (asset tiles, collection cards). Square corners, constant
 * 20×20 icon slot across sizes, and a spinner in the loading state.
 *
 * @example
 * <HoverActionButton
 *   aria-label="Open menu"
 *   hierarchy={HoverActionButtonHierarchy.Secondary}
 *   icon={<DotsVerticalIcon size={20} />}
 *   onPress={openMenu}
 * />
 */
function HoverActionButton(props: HoverActionButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) {
  const {
    'aria-label': ariaLabel,
    icon,
    size = HoverActionButtonSize.Sm,
    hierarchy = HoverActionButtonHierarchy.Primary,
    isLoading = false,
    isDisabled = false,
    className,
    ...other
  } = props;

  const isDestructive = hierarchy === HoverActionButtonHierarchy.Destructive;

  const { disabledProps, tooltipTriggerClassName } = useTooltipDisabledProps({ isDisabled, isLoading });

  return (
    <AriaButton
      {...other}
      ref={ref}
      aria-label={isLoading ? 'Loading' : ariaLabel}
      className={composeRenderProps(className, (classNameProp, renderProps) =>
        cx(
          'vzt-hover-action-button',
          hoverActionButtonClasses({
            size,
            hierarchy,
            // `isDisabled` comes from the prop, not render props: loading also
            // disables the underlying button, but must keep the loading look.
            isDisabled,
            isFocusVisible: renderProps.isFocusVisible,
            isHovered: renderProps.isHovered,
            isLoading,
          }),
          tooltipTriggerClassName,
          classNameProp,
        ),
      )}
      {...disabledProps}
    >
      {isLoading ? (
        <RadialProgress
          isIndeterminate
          aria-label="Loading"
          size={8}
          fillClassName={isDestructive ? 'stroke-white' : undefined}
          barClassName={isDestructive ? 'stroke-error-400' : undefined}
        />
      ) : (
        icon
      )}
    </AriaButton>
  );
}

const _HoverActionButton = React.forwardRef<HTMLButtonElement, HoverActionButtonProps>(HoverActionButton);

export { _HoverActionButton as HoverActionButton };
